import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from '@/shared/ui';
import { Button } from '@/shared/ui/components/shadcn/button';
import Badge from '@/shared/ui/components/basic/Badge';
import type { Vistory, VistoryRoom } from '../types/vistory.types';
import type { PropertyNestedResponse } from '@services/housien/types-property.d';
import { deriveRoomsFromProperty } from '../utils/vistory-rooms';
import { VistoryService } from '../services/vistory-service';
import { VistoryRoomCard } from './VistoryRoomCard';

interface Props {
  vistory: Vistory;
  agencyId: string;
  property: PropertyNestedResponse | null;
  onUpdate: () => void;
}

/**
 * Editor for per-room photo organization. Shows room list with per-room
 * uploader/gallery, plus an "Auto-generate from property" action.
 */
export function VistoryRoomEditor({ vistory, agencyId, property, onUpdate }: Props) {
  const { t } = useTranslation('features/vistories');
  const [generating, setGenerating] = useState(false);
  const rooms = vistory.rooms ?? [];

  async function handleAutoGenerate() {
    if (!property) return;
    setGenerating(true);
    try {
      const derived = deriveRoomsFromProperty(property);
      await VistoryService.updateVistory(agencyId, vistory.id, { rooms: derived });
      onUpdate();
    } finally { setGenerating(false); }
  }

  const showAutoGenerate = rooms.length === 0 && property !== null;

  return (
    <div className="flex flex-col gap-[var(--space-3)]">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-[var(--space-2)]">
          <Text size="sm" weight="medium" className="text-[var(--text-primary)]">
            {t('vistory.room_photos')}
          </Text>
          <Badge color="var(--text-secondary)" backgroundColor="var(--surface-tertiary)" size="sm">
            {rooms.length}
          </Badge>
        </div>
        {showAutoGenerate && (
          <Button variant="outline" size="sm" onClick={handleAutoGenerate} disabled={generating}>
            {t('vistory.auto_rooms')}
          </Button>
        )}
      </div>

      {rooms.length === 0 && !showAutoGenerate && (
        <Text size="xs" className="text-[var(--text-tertiary)]">
          {t('vistory.no_rooms')}
        </Text>
      )}

      <div className="flex flex-col gap-[var(--space-3)]">
        {rooms.map((room) => (
          <VistoryRoomCard
            key={room.id}
            room={room}
            vistory={vistory}
            agencyId={agencyId}
            onUpdate={onUpdate}
          />
        ))}
      </div>
    </div>
  );
}
